import React, { useState, useEffect } from "react";
import { Buffer } from "buffer";
import { PageHeader } from "@ant-design/pro-components";
import {
    Layout,
    Breadcrumb,
    Row,
    Col,
    Form,
    Input,
    Select,
    Button,
    Upload,
    Tabs,
    Space,
    Modal,
    ConfigProvider,
    message,
    Grid,
} from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "react-phone-input-2/lib/style.css";
import { useSelector } from "react-redux";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";
import { ProCard, ProTable } from "@ant-design/pro-components";
import Loader from "react-loader-spinner";
import { userApi } from "../../../services/actions/users";
import { notificationAPI } from "../../../services/actions/notifications";
import { DeleteOutlined, ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import ptBRIntl from "antd/lib/locale/pt_BR";
import { Groups } from "./Groups";
import { Roles } from "./Roles";
import { Permissions } from "./Permissions";
import { Unit } from "./Unit";
import { checkPermissions } from "../../../utils/permission";
import PermissionWrapperComponent from "../../../components/PermissionWrapperComponent";

const { Content } = Layout;
const { confirm } = Modal;
const tailFormItemLayout = {
    wrapperCol: {
        xs: {
            span: 24,
            offset: 0,
        },
        sm: {
            span: 16,
            offset: 8,
        },
    },
};

export default function EditUser() {
    const fetchUser = async () => {
        try {
            setLoading(true);
            const response = await userApi.getById(id, company.unitId);
            if (response.status === 200) {
                setUser(response.data);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        fetchUser();
    }, []);

    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);

    const [formUser] = Form.useForm();
    const [formPassword] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    // Abas laterais viram abas no topo em telas < lg (tablet retrato/celular)
    const screens = Grid.useBreakpoint();

    // Read the active tab from URLSearchParams
    const urlParams = new URLSearchParams(location.search);
    const activeKey = urlParams.get("tab") || "1";

    const handleTabChange = (key) => {
        const newUrlParams = new URLSearchParams(location.search);
        newUrlParams.set("tab", key);

        navigate({
            pathname: location.pathname,
            search: newUrlParams.toString(),
        });
    };
    const permissionsFromUser = useSelector((state) => state.user.permissions);
    const company = useSelector((state) => state.user.company);

    const routes = [
        {
            breadcrumbName: "Usuários e Permissões",
            href: "/usuarios-e-permissoes",
        },
        {
            breadcrumbName: "editar",
        },
    ];

    const handleSubmitUpdateDataUser = async () => {
        confirm({
            title: "Deseja atualizar as informações deste usuário?",
            icon: <ExclamationCircleOutlined />,
            content: "Veja se as informações estão corretas",
            cancelText: "Cancelar",
            okText: "Sim",
            onOk: async () => {
                try {
                    setLoading(true)
                    const values = formUser.getFieldsValue();
                    const update = await userApi.update(id, { ...values, unitId: company.unitId });
                } catch (e) {
                    setError(e.message);
                } finally {
                    fetchUser()
                    setLoading(false)
                }
            }
        });
    }

    const handleRequestNewPassword = async () => {
        try {
            setLoading(true);
            await notificationAPI.create({
                recipient: id,
                type: 'info',
                title: 'Senha Atualizada',
                content: 'Sua senha foi alterada pelo administrador, caso voce nao saiba a nova senha entre em contato.'
            });
            message.info("Notificação de troca de senha foi enviada!");
        } catch (e) {
            setError(e.message);
            message.error("Erro ao enviar notificação de troca de senha!");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitUpdateDataPassword = async () => {
        confirm({
            title: "Deseja atualizar a senha deste usuário?",
            icon: <ExclamationCircleOutlined />,
            cancelText: "Cancelar",
            okText: "Sim",
            onOk: async () => {
                try {
                    setLoading(true)
                    const { confirmPassword, password } = formPassword.getFieldsValue();
                    await userApi.updatePassword(id, password, confirmPassword, company.unitId);
                    message.info("A senha foi atualizada com sucesso");
                } catch (e) {
                    setError(e.message);
                } finally {
                    setLoading(false)
                    formPassword.resetFields();
                }
            }
        });
    }

    const handleDeleteUser = async () => {
        confirm({
            title: `Deseja desativar este usuário ${user.firstName} ?`,
            content: "O usuário não poderá mais acessar o sistema. É possível reativá-lo depois.",
            icon: <ExclamationCircleOutlined />,
            cancelText: "Cancelar",
            okText: "Sim",
            onOk: async () => {
                try {
                    setLoading(true)
                    await userApi.delete(id, company.unitId);
                    message.info("Usuário desativado com sucesso!");
                    navigate("/usuarios-e-permissoes")
                } catch (e) {
                    setError(e.message);
                } finally {
                    setLoading(false)
                }
            }
        });
    }

    const handleReactivateUser = async () => {
        confirm({
            title: `Deseja reativar este usuário ${user.firstName} ?`,
            content: "O usuário voltará a ter acesso ao sistema.",
            icon: <ExclamationCircleOutlined />,
            cancelText: "Cancelar",
            okText: "Sim",
            onOk: async () => {
                try {
                    setLoading(true)
                    await userApi.activate(id, company.unitId);
                    message.info("Usuário reativado com sucesso!");
                    navigate("/usuarios-e-permissoes")
                } catch (e) {
                    setError(e.message);
                } finally {
                    setLoading(false)
                }
            }
        });
    }

    const unitTabItem = checkPermissions(
        permissionsFromUser,
        company,
        ['MANAGE_COMPANIES'],
        {
            key: "5",
            label: "Unidades",
            children: <Unit user={user} />,
        }
    );

    const permissionTabItems = checkPermissions(
        permissionsFromUser,
        company,
        ['MANAGE_ROLES'],
        [
            {
                key: "2",
                label: "Grupos",
                children: <Groups user={user} />,
            },
            {
                key: "3",
                label: "Papeis",
                children: <Roles user={user} />,
            },
            {
                key: "4",
                label: "Permissões",
                children: <Permissions user={user} />,
            },
        ]
    );

    return (

        <LayoutTopSide>
            <TopBar />
            <Layout style={{ overflow: "hidden" }}>
                <DrawerSider />
                <Layout style={{ padding: "0 24px 24px", height: "100%", overflowY: "auto" }}>
                    <PageHeader
                        style={{ margin: "16px 0 5px 0" }}
                        title="Editar usuário"
                        onBack={() => window.history.back()}
                        subTitle="Você pode editar um perfil de usuário para cada colaborador com permissões especiais."
                        breadcrumb={
                            <Breadcrumb>
                                {routes.map((a) =>
                                    a.hasOwnProperty("href") ? (
                                        <Breadcrumb.Item key={Math.random()}>
                                            <a href onClick={() => navigate(a.href)}>
                                                {a.breadcrumbName}
                                            </a>
                                        </Breadcrumb.Item>
                                    ) : (
                                        <Breadcrumb.Item>{a.breadcrumbName}</Breadcrumb.Item>
                                    )
                                )}
                            </Breadcrumb>
                        }
                    />
                    <Content
                        className="site-layout-background"
                        style={{
                            padding: 24,
                            margin: 0,
                            minHeight: 280,
                            background: "var(--cp-surface)",
                        }}
                    >
                        {loading ? <Loader type={'Circles'} /> : user ?
                            <Tabs
                                tabPosition={screens.lg ? 'left' : 'top'}
                                defaultActiveKey="1"
                                activeKey={activeKey}
                                onChange={handleTabChange}
                                items={[
                                    {
                                        key: "1",
                                        label: "Informações e Segurança",
                                        children: (<>
                                    <ProCard colSpan={24} gutter={16} extra={
                                        <PermissionWrapperComponent permissions={['DELETE_USER']}>
                                            {user.active === false ? (
                                                <Button icon={<UndoOutlined />} onClick={() => handleReactivateUser()} type={"primary"}>Reativar usuário</Button>
                                            ) : (
                                                <Button icon={<DeleteOutlined />} onClick={() => handleDeleteUser()} danger>Desativar usuário</Button>
                                            )}
                                        </PermissionWrapperComponent>
                                    }>
                                        <PermissionWrapperComponent permissions={['UPDATE_USER']}>
                                            <ProCard colSpan={12} title={"Dados pessoais"}>
                                                <Form
                                                    onFinish={handleSubmitUpdateDataUser}
                                                    form={formUser}
                                                    scrollToFirstError
                                                    labelCol={{ flex: "110px" }}
                                                    wrapperCol={{ flex: "auto" }}
                                                    labelAlign="left"
                                                    initialValues={{
                                                        firstName: user.firstName,
                                                        lastName: user.lastName,
                                                        username: user.username,
                                                        email: user.email,
                                                    }}
                                                >
                                                    <Form.Item
                                                        name="firstName"
                                                        label="Nome"
                                                        rules={[{
                                                            required: true,
                                                            message: "Por favor insira o nome!",
                                                            whitespace: false
                                                        }]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name="lastName"
                                                        label="Sobrenome"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Por favor insira o sobrenome!",
                                                                whitespace: false,
                                                            },
                                                        ]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name="username"
                                                        label="Usuário"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Por favor insira o nome de acesso do usuário!",
                                                                whitespace: false,
                                                            },
                                                        ]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name="email"
                                                        label="E-mail"
                                                        rules={[
                                                            {
                                                                type: "email",
                                                                message:
                                                                    "E-mail que você digitou não é um e-mail válido!",
                                                            },
                                                            {
                                                                required: true,
                                                                message: "Por favor insira o e-mail!",
                                                            },
                                                        ]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item>
                                                        <Button type="primary" htmlType="submit">
                                                            Salvar Alterações
                                                        </Button>
                                                    </Form.Item>
                                                </Form>
                                            </ProCard>
                                        </PermissionWrapperComponent>
                                        <ProCard colSpan={12} title={"Notificar troca e recuperação de senha"}>
                                            <PermissionWrapperComponent permissions={["RESET_PASSWORD"]}>
                                                <Form
                                                    form={formPassword}
                                                    scrollToFirstError
                                                    onFinish={handleSubmitUpdateDataPassword}
                                                    labelCol={{ flex: "110px" }}
                                                    wrapperCol={{ flex: "auto" }}
                                                    labelAlign="left"
                                                >
                                                    <Form.Item
                                                        name="password"
                                                        label="Senha"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Por favor insira uma senha!",
                                                            },
                                                            {
                                                                min: 8,
                                                                message: "A senha deve ter no mínimo 8 caracteres!",
                                                            },
                                                        ]}
                                                        hasFeedback
                                                    >
                                                        <Input.Password placeholder="*********" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="confirmPassword"
                                                        label="Confirme"
                                                        dependencies={["password"]}
                                                        hasFeedback
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Por favor, confirme a senha!",
                                                            },
                                                            ({ getFieldValue }) => ({
                                                                validator(_, value) {
                                                                    if (!value || getFieldValue("password") === value) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    return Promise.reject(
                                                                        new Error("As duas senhas que você digitou não coincidem!")
                                                                    );
                                                                },
                                                            }),
                                                        ]}
                                                    >
                                                        <Input.Password placeholder="*********" />
                                                    </Form.Item>

                                                    <Form.Item>
                                                        <Space wrap>
                                                            <PermissionWrapperComponent permissions={["RESET_PASSWORD"]}>
                                                                <Button type="primary" htmlType="submit">
                                                                    Salvar Senha
                                                                </Button>
                                                            </PermissionWrapperComponent>
                                                            <PermissionWrapperComponent
                                                                permissions={['REQUEST_PASSWORD_CHANGE']}>
                                                                <Button type="default"
                                                                    onClick={handleRequestNewPassword}>
                                                                    Enviar notificação de atualização de senha
                                                                </Button>
                                                            </PermissionWrapperComponent>
                                                        </Space>
                                                    </Form.Item>
                                                </Form>
                                            </PermissionWrapperComponent>

                                        </ProCard>
                                    </ProCard>
                                        </>),
                                    },
                                    ...(permissionTabItems || []),
                                    ...(unitTabItem ? [unitTabItem] : []),
                                ]}
                            /> : <p>Erro ao recuperar informações do usuário</p>}
                    </Content>
                </Layout>
            </Layout>
        </LayoutTopSide>

    );
}
